import { endOfDay, format, hoursToMilliseconds, startOfDay, subDays } from 'date-fns'
import { FC, ReactNode } from 'react'
import { TFunction, useTranslation } from 'react-i18next'

import { Box, Chip, Stack, Typography } from '@mui/material'
import { visuallyHidden } from '@mui/utils'

import { TrendIndicator } from '../../../../components/TrendIndicator/TrendIndicator'
import { formatEventKpiPercentage, formatKpiRange, getEventKpiChipColor } from '../../helpers/eventKpiDisplay'

/**
 * Renders a KPI chip label. For a 0 → non-zero transition the change is
 * ±Infinity (JSON can't carry it, so it's synthesized client-side). A literal
 * "+∞%" reads poorly, so surface a directional arrow only — consistent with the
 * Event Stats "Popularity Change" column and the calendar strip TrendIndicator.
 * A visually hidden label keeps the direction available to assistive tech since
 * the arrow alone carries no text. NaN/finite values keep the textual format.
 */
const renderKpiChipLabel = (value: number, prefix: string, t: TFunction): ReactNode => {
  if (value === Infinity || value === -Infinity) {
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', '& .MuiSvgIcon-root': { fontSize: '18px', m: '-3px' } }}>
        {prefix}
        <Box component="span" sx={visuallyHidden}>
          {` ${value > 0 ? t('live-events:kpi_change_increased_sr') : t('live-events:kpi_change_decreased_sr')}`}
        </Box>
        <TrendIndicator value={value} direction allowInfinity sign={false} color={false} />
      </Box>
    )
  }
  return formatEventKpiPercentage(value, prefix)
}

interface EventKPIIndicatorProps {
  revenueChange?: number
  downloadChange?: number
  dauChange?: number
  startTime: number
  endTime?: number
  /**
   * Overrides the ended check, which otherwise reads `endTime` as a local-time day and counts the event as ended only after that
   * local day is over. For callers whose `endTime` is already the real end of the event, e.g. a UTC day end.
   */
  eventEnded?: boolean
  hideYear?: boolean
  hideStatusChip?: boolean
  showComparisonRange?: boolean
  showYearInComparison?: boolean
  showAllKpiChips?: boolean
}

export const EventKPIIndicator: FC<EventKPIIndicatorProps> = ({
  revenueChange,
  downloadChange,
  dauChange,
  startTime,
  endTime,
  eventEnded,
  hideYear = false,
  hideStatusChip = false,
  showComparisonRange = false,
  showYearInComparison = false,
  showAllKpiChips = false,
}) => {
  const { t } = useTranslation()

  // Format dates - handle same month, different month, and different year cases
  const startDate = new Date(startTime)
  const endDate = endTime ? new Date(endTime) : startDate
  // Multi-day = start and end are on different calendar days (avoids off-by-one when stored as day-start timestamps)
  const isMultiDay = !!endTime && startDate.toDateString() !== endDate.toDateString()
  const isSameYear = startDate.getFullYear() === endDate.getFullYear()
  const isSameMonth = startDate.getMonth() === endDate.getMonth() && isSameYear

  const formattedDate = isMultiDay
    ? isSameMonth
      ? `${format(startDate, 'MMM d')} - ${format(endDate, 'd')}`
      : isSameYear || hideYear
      ? `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d')}`
      : `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`
    : format(startDate, 'MMM d')

  if (startTime > Date.now() || (revenueChange === undefined && downloadChange === undefined && dauChange === undefined)) return null

  const eventHasEnded = eventEnded ?? (!!endTime && endOfDay(new Date(endTime)).getTime() < Date.now())
  const statusLabel = isMultiDay ? (eventHasEnded ? t('live-events:kpi_explanation_status_final') : t('live-events:kpi_explanation_status_in_progress')) : null

  const isOngoing = isMultiDay && !eventHasEnded

  let comparisonText: string | undefined
  if (showComparisonRange && isOngoing) {
    comparisonText = t('live-events:kpi_explanation_ongoing_summary')
  } else if (showComparisonRange) {
    const duringStart = startOfDay(startDate)
    const duringEnd = isMultiDay ? (eventHasEnded ? endOfDay(endDate) : endOfDay(new Date())) : duringStart
    const windowDays = Math.floor((duringEnd.getTime() - duringStart.getTime()) / hoursToMilliseconds(24)) + 1
    const beforeEnd = subDays(duringStart, 1)
    const beforeStart = subDays(beforeEnd, Math.max(0, windowDays - 1))
    comparisonText = `${formatKpiRange(duringStart, duringEnd, showYearInComparison)} vs ${formatKpiRange(beforeStart, beforeEnd, showYearInComparison)}`
  }

  const statusChipSx = {
    fontSize: '10px',
    height: '18px',
    fontWeight: 'bold',
    bgcolor: eventHasEnded ? 'success.main' : 'warning.main',
    color: 'white',
    '& .MuiChip-label': { px: '6px' },
  } as const

  return (
    <Stack direction="column" alignItems={showComparisonRange ? 'center' : 'flex-end'} spacing={0.5} sx={{ flexShrink: 0 }}>
      {showComparisonRange && (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" flexWrap="wrap" sx={{ columnGap: 1, rowGap: 0.5 }}>
          <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
            {comparisonText}
          </Typography>
          {!hideStatusChip && statusLabel && <Chip label={statusLabel} size="small" sx={statusChipSx} />}
        </Stack>
      )}
      <Stack direction="row" spacing={0.5} justifyContent={showComparisonRange ? 'center' : 'flex-end'}>
        {!showComparisonRange && (
          <Typography variant="caption" sx={{ fontSize: '11px', fontWeight: 'bold', pr: 0.5, whiteSpace: 'nowrap' }}>
            {formattedDate}
          </Typography>
        )}
        {revenueChange !== undefined && (
          <Chip
            label={isOngoing ? 'REV' : renderKpiChipLabel(revenueChange, 'REV', t)}
            color={isOngoing ? 'default' : getEventKpiChipColor(revenueChange)}
            size="small"
            sx={{ fontWeight: 'bold', fontSize: '11px' }}
          />
        )}
        {downloadChange !== undefined && (
          <Chip
            label={isOngoing ? 'DL' : renderKpiChipLabel(downloadChange, 'DL', t)}
            color={isOngoing ? 'default' : getEventKpiChipColor(downloadChange)}
            size="small"
            sx={{ fontWeight: 'bold', fontSize: '11px' }}
          />
        )}
        {(dauChange !== undefined || showAllKpiChips) && (
          <Chip
            label={isOngoing || dauChange === undefined ? 'DAU' : renderKpiChipLabel(dauChange, 'DAU', t)}
            color={isOngoing || dauChange === undefined ? 'default' : getEventKpiChipColor(dauChange)}
            size="small"
            sx={{ fontWeight: 'bold', fontSize: '11px' }}
          />
        )}
      </Stack>
      {!showComparisonRange && !hideStatusChip && statusLabel && <Chip label={statusLabel} size="small" sx={statusChipSx} />}
    </Stack>
  )
}
